import React from "react";
import { Constants } from "@eagerdog/constants";
import { Link } from "react-router-dom";
import { IFeedPostItem } from "@eagerdog/interfaces";
import moment from "moment";

import Icon from "src/components/Icon/Icon";
import EventActivityCard from "./EventActivityCard";
import ClubActivityCard from "./ClubActivityCard";
import DogActivityCard from "./DogActivityCard";

import styles from "./ActivityBlock.module.scss";

interface IProps {
    post: IFeedPostItem;
}

const formatDates = (start?: Date | string, end?: Date | string): string => {
    if (!start) return "";
    const s = moment(start);
    if (!end || s.isSame(end, "day")) return s.format("MMM D, YYYY");
    const e = moment(end);
    return s.isSame(e, "month")
        ? `${s.format("MMM D")}–${e.format("D, YYYY")}`
        : `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
};

const formatPlace = (city?: string, state?: string): string =>
    [city, state].filter(Boolean).join(", ");

/** Where the activity happened: the event, course, club, or dog behind the post. */
export const activityLink = (post: IFeedPostItem): string | undefined => {
    const a = post.activity;
    if (!a) return undefined;
    switch (post.post_type) {
        case Constants.post_type.event_published:
        case Constants.post_type.event_registered:
        case Constants.post_type.result_entered:
            return `/e/${a.event_handle || a.event_id || post.linked_event_id}`;
        case Constants.post_type.course_enrolled:
            return a.course_handle || a.course_id ? `/c/${a.course_handle || a.course_id}` : undefined;
        case Constants.post_type.club_followed:
            return a.target_id ? `/k/${a.target_id}` : undefined;
        case Constants.post_type.dog_followed:
            return a.target_id ? `/dogs/${a.target_id}` : undefined;
        default:
            return undefined;
    }
};

// Compact card for a system-generated activity post. Links through to the
// event / course / club / dog the activity is about.
const ActivityBlock: React.FC<IProps> = ({ post }) => {
    const a = post.activity;
    if (!a) return null;

    // Results render through RunSummary; the activity is only there for links.
    if (post.post_type === Constants.post_type.result_entered) return null;

    const link = activityLink(post);

    // Events get the full summary card (date tile, pills, registration line).
    if ((post.post_type === Constants.post_type.event_published || post.post_type === Constants.post_type.event_registered) && link) {
        return <EventActivityCard post={post} link={link} />;
    }

    // Followed club: cover, logo, name, location, blurb.
    if (post.post_type === Constants.post_type.club_followed && link) {
        return <ClubActivityCard post={post} link={link} />;
    }

    // Followed dog: profile-style identity band.
    if (post.post_type === Constants.post_type.dog_followed && link) {
        return <DogActivityCard post={post} link={link} />;
    }

    let icon: string;
    let title: string | undefined;
    let line: string | undefined;
    let sub: string | undefined;
    let image: string | undefined;

    switch (post.post_type) {
        case Constants.post_type.course_enrolled:
            icon = "school";
            title = a.course_title;
            line = [a.club_name, formatDates(a.start_date, a.end_date)].filter(Boolean).join(" · ");
            sub = formatPlace(a.city, a.state);
            break;
        case Constants.post_type.club_followed:
            icon = "group";
            title = a.target_name;
            line = formatPlace(a.city, a.state);
            image = a.target_image_url;
            break;
        case Constants.post_type.dog_followed:
            icon = "paw";
            title = a.target_name;
            line = a.target_breed;
            image = a.target_image_url;
            break;
        default:
            return null;
    }

    const content = (
        <>
            <div className={styles.thumb}>
                {image ? <img src={image} alt="" /> : <Icon type={icon} className={styles.icon} />}
            </div>
            <div className={styles.text}>
                {title && <span className={styles.title}>{title}</span>}
                {line && <span className={styles.line}>{line}</span>}
                {sub && <span className={styles.sub}>{sub}</span>}
            </div>
            {link && <Icon type="caretRight" className={styles.caret} />}
        </>
    );

    return link
        ? <Link to={link} className={styles.ActivityBlock}>{content}</Link>
        : <div className={styles.ActivityBlock}>{content}</div>;
};

export default ActivityBlock;
